import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppInputNumber, AppModal, AppSelect, AppTextField } from '@/components/atoms';
import { useRezeptFreeMedicalTypes } from '@/hooks/main/useRezeptFreeMedicalTypes';
import { useSaveRezeptFreeMedical } from '@/hooks/main/useSaveRezeptFreeMedical';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import type { RezeptFreeMedicalType, RezeptInvoiceListItem } from '@/services/main/rezept';

type FormState = { name: string; fee: number | null };

export interface RezeptInvoiceEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: RezeptInvoiceListItem | null;
}

/** Format dropdown label: "{type_name}　{category_name}　{name}　{price}円" */
function buildTypeLabel(t: RezeptFreeMedicalType): string {
  const parts = [t.type_name, t.category_name, t.name].filter(Boolean);
  return `${parts.join('　')}　${t.price.toLocaleString()}円`;
}

const RezeptInvoiceEditModal: React.FC<RezeptInvoiceEditModalProps> = ({ isOpen, onClose, record }) => {
  const toast = useAppToast();
  const { data: freeMedicalTypes = [] } = useRezeptFreeMedicalTypes();
  const saveMutation = useSaveRezeptFreeMedical();

  const [selectedTypeId, setSelectedTypeId] = useState<number>(0);
  const [name, setName] = useState('');
  const [fee, setFee] = useState<number | null>(null);
  const [nameError, setNameError] = useState('');
  const [feeError, setFeeError] = useState('');
  const [initialFormData, setInitialFormData] = useState<FormState>({ name: '', fee: null });

  // Populate from existing record when modal opens
  useEffect(() => {
    if (!isOpen || !record) return;
    const initial: FormState = {
      name: record.free_medical_treatment_name ?? '',
      fee: typeof record.free_medical_treatment_fee === 'number' ? record.free_medical_treatment_fee : null,
    };
    setSelectedTypeId(0);
    setName(initial.name);
    setFee(initial.fee);
    setInitialFormData(initial);
    setNameError('');
    setFeeError('');
  }, [isOpen, record]);

  const formState = useMemo<FormState>(() => ({ name, fee }), [name, fee]);
  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(formState, initialFormData);

  const handleRequestClose = useCallback(() => {
    confirmIfChanged(() => {
      clearFormChange();
      onClose();
    });
  }, [confirmIfChanged, clearFormChange, onClose]);

  // 削除 visible only when name is non-empty (BD spec: only name check)
  const hasExistingData = !!(record?.free_medical_treatment_name);

  const handleSelectType = (id: number) => {
    setSelectedTypeId(id);
    if (!id) {
      return;
    }
    const type = freeMedicalTypes.find(t => t.id === id);
    if (type) {
      setName(type.name);
      setFee(type.price);
      setNameError('');
      setFeeError('');
    }
  };

  const handleFeeChange = (v: string | number | null) => {
    if (v === null || v === '') {
      setFee(null);
      return;
    }
    const raw = typeof v === 'number' ? v : parseInt(String(v), 10);
    setFee(isNaN(raw) ? null : raw);
  };

  const validate = (): boolean => {
    let valid = true;
    if (!name.trim()) {
      setNameError('自由診療名を入れてください');
      valid = false;
    } else {
      setNameError('');
    }
    if (fee === null || fee < 0) {
      setFeeError('正数で入力してください');
      valid = false;
    } else {
      setFeeError('');
    }
    return valid;
  };

  const handleOk = async () => {
    if (!record || !validate()) return;
    try {
      await saveMutation.mutateAsync({
        rezeptId: record.id,
        payload: {
          free_medical_treatment_name: name.trim() || null,
          free_medical_treatment_fee: fee,
        },
      });
      toast.success('保存しました。');
      clearFormChange();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, '保存に失敗しました。'));
    }
  };

  const handleDelete = async () => {
    if (!record) return;
    try {
      await saveMutation.mutateAsync({
        rezeptId: record.id,
        payload: { free_medical_treatment_name: null, free_medical_treatment_fee: null },
      });
      toast.success('削除しました。');
      clearFormChange();
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, '削除に失敗しました。'));
    }
  };

  if (!record) return null;

  return (
    <AppModal
      isOpen={isOpen}
      onClose={handleRequestClose}
      title="領収書・請求書編集"
      maxWidth={520}
      onOk={handleOk}
      okText="適用"
      confirmLoading={saveMutation.isPending}
      onDelete={hasExistingData ? handleDelete : undefined}
    >
      <div className="space-y-4">
        {/* 1. 自由診療リスト dropdown */}
        <div className="flex items-center gap-3">
          <span className="w-28 shrink-0 text-sm font-medium">自由診療リスト</span>
          <AppSelect
            className="flex-1"
            value={selectedTypeId}
            onChange={v => handleSelectType(typeof v === 'number' ? v : 0)}
            options={[
              { value: 0, label: '選択してください' },
              ...freeMedicalTypes.map(t => ({ value: t.id, label: buildTypeLabel(t) })),
            ]}
            allowClear={false}
          />
        </div>

        {/* 2. 自由診療名 */}
        <div className="flex items-start gap-3">
          <span className="w-28 shrink-0 text-sm font-medium pt-1">自由診療名</span>
          <div className="flex-1">
            <AppTextField
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              placeholder="自由診療名を入力"
            />
            {nameError && <p className="mt-1 text-xs text-red-500">{nameError}</p>}
          </div>
        </div>

        {/* 3. 金額 */}
        <div className="flex items-start gap-3">
          <span className="w-28 shrink-0 text-sm font-medium pt-1">金額</span>
          <div>
            <span className="inline-flex items-center gap-1">
              <AppInputNumber
                min={0}
                step={1}
                controls={false}
                className="w-32 text-right"
                value={fee ?? undefined}
                parser={val => {
                  const n = parseInt(String(val), 10);
                  return isNaN(n) ? ('' as unknown as number) : n;
                }}
                onChange={v => {
                  handleFeeChange(v);
                  if (feeError) setFeeError('');
                }}
              />
              <span className="text-sm">円</span>
            </span>
            {feeError && <p className="mt-1 text-xs text-red-500">{feeError}</p>}
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default RezeptInvoiceEditModal;
